import { isAndroid, isIos } from '@yembo/yemblocks-core/dist/_util';
import { ReactNode, useContext, useState } from 'react';
import { ControlPosition } from 'react-draggable';
import { useEmployeeQuery } from 'src/queries/Employee';
import { useJobQuery } from 'src/queries/Job';
import { useCompanyQuery } from 'src/queries/Company';
import { useCustomizationsQuery } from 'src/queries/Company/customizations';
import { useImagesQueryForOffline } from 'src/queries/Image';
import { useInsuredQuery } from 'src/queries/Insured';
import { useRoomsQueryForOffline } from 'src/queries/Room';
import { useVideosQueryForOffline } from 'src/queries/Video';
import { useItemsForJob } from 'src/react/_util/useItemsForJob';
import { CHAT_BOX_HEIGHT, CHAT_BOX_WIDTH, FloatingChatBox } from 'src/react/FloatingChatBox';
import { isIntercomEnabled } from 'src/util/intercom';
import { AuthUserContext } from '../AuthUserContext';
import { ChatBoxContext } from './context';

export const ChatBoxProvider = ({ children }: { children: ReactNode }) => {
  const [jobKey, setJobKey] = useState<string>();
  const [defaultPosition, setDefaultPosition] = useState<ControlPosition | undefined>();
  const { authUser } = useContext(AuthUserContext);

  const { data: job } = useJobQuery(jobKey);

  const employeeKey = job ? (job.employeeKey ?? '') : '';

  const { data: employee } = useEmployeeQuery(employeeKey);
  const { data: rooms } = useRoomsQueryForOffline(jobKey);
  const { data: videos } = useVideosQueryForOffline(jobKey);
  const { data: images } = useImagesQueryForOffline(jobKey);
  const { data: company } = useCompanyQuery(job?.companyKey);

  const items = useItemsForJob(jobKey, authUser, company);

  const { data: insured } = useInsuredQuery(job?.insuredKey);
  const { data: customizations } = useCustomizationsQuery(job?.companyKey);

  const closeChat = () => {
    setJobKey(undefined);
    setDefaultPosition(undefined);
  };

  const isMobile = isIos() || isAndroid();

  const openChat = (jobKey: string) => {
    setJobKey(jobKey);
    setDefaultPosition({
      x: isMobile ? 16 : window.innerWidth - CHAT_BOX_WIDTH - 20 - (isIntercomEnabled ? 72 : 0),
      y: isMobile ? 16 : window.innerHeight - CHAT_BOX_HEIGHT - 20,
    });
  };

  const isChatOpen = Boolean(jobKey);

  return (
    <ChatBoxContext.Provider value={{ openChat, isChatOpen }}>
      {children}
      {isChatOpen && (
        <FloatingChatBox
          employee={employee}
          job={job}
          rooms={rooms}
          videos={videos}
          images={images}
          items={items}
          insured={insured}
          jobCompany={company}
          closeChat={closeChat}
          defaultPosition={defaultPosition}
          customizations={customizations}
        />
      )}
    </ChatBoxContext.Provider>
  );
};
